import { useAuditCellHistory } from "@api/generated/audit/audit";
import { getGenealogyExportUrl, useGenealogyPerformance, useGenealogyPlaces, useGenealogySheet } from "@api/generated/genealogy/genealogy";
import type { Ancestor, AuditRow, GenotypeSheet, MarkerValue } from "@api/generated/model";
import { useSeason } from "@app/workspace";
import {
  FIELDS,
  GenebankTotals,
  GenotypeSearch,
  Kpis,
  LineLink,
  MarkersCard,
  PedigreeCard,
  PerformanceCard,
  PlacesCard,
  PlotsTab,
  RowsTab,
  SistersCard,
  useAncestry,
} from "@features/genealogy/GenotypeParts";
import { keyText, markText } from "@features/genealogy/genotype";
import { SeedPostTab } from "@features/genealogy/SeedPost";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import "@features/genealogy/genealogy.css";
import { Button, Loader, Tabs } from "@mantine/core";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { changeText } from "@ui/audit";
import { formatNumber, fullTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["overview", "years", "pedigree", "molecular", "genebank", "seedPost", "dna", "journal"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/genealogy/genotype")({
  validateSearch: z.object({
    paz: z.number().int().optional(),
    sid: z.number().int().optional(),
    tab: z.enum(TABS).optional(),
    performance: z.enum(["environment", "annual"]).default("environment"),
    post: z.enum(["shipments", "arrivals"]).optional(),
    partner: z.number().int().optional(), // MagPosta: only this partner's (CAz) shipments or arrivals
  }),
  component: GenotypePage,
});

/** Genealógia → Genotípus-adatlap: everything about one line (PAz + SID) on one screen, from the register record
 *  to its plots across the years, the gene bank, the seed post, its pedigree, sister lines and markers. */
function GenotypePage() {
  const { t } = useTranslation();
  const { paz, sid, tab } = Route.useSearch();
  const { ev } = useSeason();
  if (paz == null || sid == null) {
    return (
      <div className="page">
        <PageHeader title={t("genotype.title")} subtitle={t("genotype.subtitle")} />
        <GenotypeSearch to="/genealogy/genotype" />
      </div>
    );
  }
  if (!ev) return <Loader size="sm" m="md" />;
  return <Sheet paz={paz} sid={sid} ev={ev} tab={tab ?? "overview"} />;
}

function Sheet({ paz, sid, ev, tab }: { paz: number; sid: number; ev: number; tab: Tab }) {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const [year, setYear] = useState(ev);
  const performanceMode = Route.useSearch().performance;
  const k = { paz, sid };
  const query = useGenealogySheet(paz, sid, { ev });
  // the overview's other reads start with the sheet, not after it
  useGenealogyPerformance(paz, sid, { fields: [...FIELDS] });
  useGenealogyPlaces(paz, sid, { ev: year });
  useAncestry(k);
  const sheet = query.data;
  if (query.isError) {
    return (
      <div className="page">
        <PageHeader title={t("genotype.title")} />
        <p className="note warn">{t("genotype.notFound", { key: keyText({ paz, sid }) })}</p>
        <GenotypeSearch to="/genealogy/genotype" />
      </div>
    );
  }
  if (!sheet) return <Loader size="sm" m="md" />;
  const r = sheet.register;
  const s = sheet.summary;
  const title = r.name ?? keyText(k);
  // names that only repeat the title are left out; the pedigree wraps on its own line
  const names = [
    r.torzs_nev && r.torzs_nev !== title && `${t("genotype.lineName")} ${r.torzs_nev}`,
    r.rov && r.rov !== title && `${t("genotype.abbreviation")} ${r.rov}`,
  ].filter(Boolean);
  const openTab = (x: Tab) => void navigate({ search: (p) => ({ ...p, tab: x }) });
  const counts: Partial<Record<Tab, number>> = {
    years: s.plots,
    genebank: s.genebank_items,
    seedPost: s.seed_shipments + s.seed_arrivals,
    dna: s.dna_samples,
  };
  return (
    <div className="page">
      <PageHeader
        title={
          <span className="hstack" style={{ gap: 8, flexWrap: "wrap" }}>
            {title}
            <span className={`chip ${r.fajta ? "g" : ""}`}>{r.fajta ? t("genotype.variety") : t("genotype.line")}</span>
            {(r.pgm || r.gh) && (
              <span className="chip">
                {[r.pgm, r.gh && t(`genotype.growthHabit.${r.gh}`, { defaultValue: r.gh })].filter(Boolean).join(" · ")}
              </span>
            )}
            {r.orsz && <span className="chip">{r.orsz}</span>}
            {s.standard && <span className="chip b">{t("genotype.standard")}</span>}
          </span>
        }
        subtitle={
          <span className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
            <span className="chip mono key-chip">PAz {paz}</span>
            <span className="chip mono key-chip">SID {sid}</span>
            {names.length > 0 && <span className="mono">{names.join(" · ")}</span>}
          </span>
        }
        item={{ kind: "genotype", title, subtitle: keyText(k) }}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            <Button component="a" href={getGenealogyExportUrl(paz, sid, { ev })} size="xs" variant="default">
              {t("genotype.allData")}
            </Button>
            <ActionMenu
              size="xs"
              label={t("genotype.lineMenu")}
              actions={[
                {
                  group: t("genotype.menuOpen"),
                  label: t("genotype.pedigreeTree"),
                  onClick: () => void navigate({ to: "/genealogy/tree", search: { paz, sid } }),
                },
                {
                  group: t("genotype.menuOpen"),
                  label: t("genotype.menuAncestors"),
                  onClick: () => void navigate({ to: "/genealogy/ancestors", search: { lines: [{ paz, sid, name: title }] } }),
                },
                {
                  group: t("genotype.menuEdit"),
                  label: t("genotype.editInRegister"),
                  onClick: () => void navigate({ to: "/genealogy/register", search: { paz, sid } }),
                },
              ]}
            />
          </div>
        }
      />
      {r.pedigre && r.pedigre !== title && (
        <p className="mono sm gt-pedigree">
          <span className="mu">{t("genotype.pedigreeLower")}</span> {r.pedigre}
        </p>
      )}
      <Tabs value={tab} onChange={(v) => v && openTab(v as Tab)} keepMounted={false}>
        <AnimatedTabsList mb="md">
          {TABS.map((x) => (
            <Tabs.Tab
              key={x}
              value={x}
              rightSection={counts[x] != null ? <span className="mu sm">{formatNumber(counts[x])}</span> : undefined}
            >
              {t(`genotype.tab.${x}`)}
            </Tabs.Tab>
          ))}
        </AnimatedTabsList>
        <Tabs.Panel value="overview">
          <div className="gt-grid">
            <div className="stack" style={{ gap: 16, minWidth: 0 }}>
              <Kpis sheet={sheet} year={ev} onTab={openTab} />
              <PlacesCard k={k} year={year} />
              <PerformanceCard
                k={k}
                onYear={setYear}
                mode={performanceMode}
                onMode={(performance) => navigate({ search: (s) => ({ ...s, performance }) })}
              />
            </div>
            <aside className="stack" style={{ gap: 16 }}>
              <PedigreeCard sheet={sheet} />
              <SistersCard sheet={sheet} />
              <MarkersCard sheet={sheet} />
            </aside>
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="years">
          <PlotsTab k={k} />
        </Tabs.Panel>
        <Tabs.Panel value="pedigree">
          <Kinship sheet={sheet} />
        </Tabs.Panel>
        <Tabs.Panel value="molecular">
          <Markers sheet={sheet} />
        </Tabs.Panel>
        <Tabs.Panel value="genebank">
          <GenebankTotals sheet={sheet} />
          <RowsTab k={k} table="koll" />
        </Tabs.Panel>
        <Tabs.Panel value="seedPost">
          <SeedPost k={k} sheet={sheet} />
        </Tabs.Panel>
        <Tabs.Panel value="dna">
          <RowsTab k={k} table="dna_source" />
        </Tabs.Panel>
        <Tabs.Panel value="journal">
          <History paz={paz} sid={sid} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}

/** MagPosta: the line's shipments and arrivals, a tab each (opening on the one that has rows), and the partner picked
 *  in the link. */
function SeedPost({ k, sheet }: { k: { paz: number; sid: number }; sheet: GenotypeSheet }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const s = sheet.summary;
  const direction = search.post ?? (!s.seed_shipments && s.seed_arrivals ? "arrivals" : "shipments");
  return (
    <SeedPostTab
      k={k}
      sheet={sheet}
      direction={direction}
      partner={search.partner}
      onDirection={(post) => navigate({ search: (x) => ({ ...x, post, partner: undefined }) })}
      onPartner={(partner) => navigate({ search: (x) => ({ ...x, partner }) })}
    />
  );
}

function Kinship({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  const ancestry = useAncestry(sheet.register).data?.ancestry;
  const columns = useMemo<Column<Ancestor>[]>(
    () => [
      {
        id: "ancestor",
        header: t("genotype.ancestor"),
        value: (a) => a.line?.name ?? a.label,
        cell: (a) => <LineLink line={a.line} text={a.label} />,
      },
      {
        id: "key",
        header: "PAz / SID",
        value: (a) => (a.line ? keyText(a.line.key) : t("genotype.notInRegister")),
        className: "mono sm",
      },
      { id: "share", header: "%", value: (a) => a.share, numeric: true, cell: (a) => formatNumber(a.share, 2) },
      { id: "occurrences", header: t("genotype.occurrences"), value: (a) => a.paths, numeric: true },
    ],
    [t],
  );
  const table = useDataTable("genealogy.genotype.ancestry", columns, ancestry);
  return (
    <div className="gt-grid">
      <section className="card">
        <h2 className="h2 inset">{t("genotype.ancestorShares")}</h2>
        <DataTable table={table} rowKey={(a) => a.ident} />
      </section>
      <aside className="stack" style={{ gap: 16 }}>
        <PedigreeCard sheet={sheet} />
        <SistersCard sheet={sheet} />
      </aside>
    </div>
  );
}

function Markers({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  const columns = useMemo<Column<MarkerValue>[]>(
    () => [
      { id: "locus", header: t("genotype.locus"), value: (m) => m.locus, className: "mono" },
      {
        id: "value",
        header: t("genotype.value"),
        value: (m) => markText(m).mark,
        cell: (m) => <span className={`chip ${markText(m).tone}`}>{markText(m).mark}</span>,
      },
      {
        id: "source",
        header: t("genotype.sourceColumn"),
        value: (m) => t(`genotype.markSource.${m.source}`),
        className: "sm mu",
      },
    ],
    [t],
  );
  const table = useDataTable("genealogy.genotype.markers", columns, sheet.markers);
  const edit = (
    <Link to="/genealogy/register" search={{ paz: sheet.register.paz, sid: sheet.register.sid, tab: "genes" }} className="sm">
      {t("genotype.editGenes")}
    </Link>
  );
  if (!sheet.markers.length)
    return (
      <p className="sm mu">
        {t("genotype.noMarkers")} {edit}
      </p>
    );
  return (
    <section className="card">
      <div className="inset hstack" style={{ justifyContent: "flex-end" }}>
        {edit}
      </div>
      <DataTable table={table} rowKey={(m) => `${m.source}-${m.locus}`} />
    </section>
  );
}

/** The line's changes in the web system: its register row (torzs) and its combination's (kombinacio: pedigree,
 *  recorded cross), newest first. */
function History({ paz, sid }: { paz: number; sid: number }) {
  const { t } = useTranslation();
  const line = useAuditCellHistory({ table: "torzs", pk: JSON.stringify({ paz, sid }) }, { query: {} }).data;
  const cross = useAuditCellHistory({ table: "kombinacio", pk: JSON.stringify({ paz }) }, { query: {} }).data;
  const rows = useMemo(() => (line && cross ? [...line, ...cross].sort((a, b) => b.ts.localeCompare(a.ts)) : undefined), [line, cross]);
  const columns = useMemo<Column<AuditRow>[]>(
    () => [
      {
        id: "time",
        header: t("genotype.time"),
        value: (r) => r.ts,
        text: (r) => fullTime(r.ts),
        className: "mono sm",
      },
      { id: "user", header: t("genotype.user"), value: (r) => r.username },
      {
        id: "table",
        header: t("genotype.table"),
        value: (r) => t(`genotype.tables.${r.table}`, { defaultValue: r.table }),
        className: "sm",
      },
      { id: "change", header: t("genotype.change"), value: (r) => changeText(r, t), className: "mono sm" },
    ],
    [t],
  );
  const table = useDataTable("genealogy.genotype.history", columns, rows);
  if (!rows) return <Loader size="sm" />;
  if (!rows.length) return <p className="sm mu">{t("genotype.noChanges")}</p>;
  return (
    <section className="card">
      <DataTable table={table} rowKey={(r) => r.id} />
    </section>
  );
}
